import React, { useState, useEffect } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import {Modal} from "@/widgets/Modal";

interface ScreenshotLightboxProps {
    isOpen: boolean;
    screenshots: string[];
    initialIndex: number;
    onClose: () => void;
    title?: string;
    description?: string;
}

// Format description to add line breaks before specific long product names (desktop only)
const formatDescription = (description: string): React.ReactNode => {
    // Skip formatting on mobile - let text wrap naturally
    const isMobile = typeof window !== "undefined" && (/iPhone|iPad|iPod|Android/i).test(navigator.userAgent);
    if (isMobile) return description;

    // Patterns to break before (remove the "| " prefix when breaking)
    const breakPatterns = [
        /\s*\|\s*(1x SCUF)/gi,
        /\s*\|\s*(1x K65 Plus)/gi,
    ];

    let formatted = description;
    breakPatterns.forEach((pattern) => {
        formatted = formatted.replace(pattern, "\n$1");
    });

    // Split by newlines and render with <br /> elements
    const lines = formatted.split("\n");
    if (lines.length === 1) return description;

    return lines.map((line, index) =>
        <React.Fragment key={index}>
            {index > 0 && <br />}
            {line}
        </React.Fragment>);
};

const ScreenshotLightbox: React.FC<ScreenshotLightboxProps> = ({
    isOpen,
    screenshots,
    initialIndex,
    onClose,
    title,
    description,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex]);

    const handlePrevious = () => {
        setCurrentIndex((prev) =>
            prev === 0 ? screenshots.length - 1 : prev - 1);
    };

    const handleNext = () => {
        setCurrentIndex((prev) =>
            prev === screenshots.length - 1 ? 0 : prev + 1);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (!isOpen) return;

        if (e.key === "ArrowLeft") {
            handlePrevious();
        } else if (e.key === "ArrowRight") {
            handleNext();
        } else if (e.key === "Escape") {
            onClose();
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen]);

    /*
     * Note: Don't manage body.style.overflow here - the legacy modal already handles it
     * Managing it in both places causes the modal counter to get out of sync
     * and can leave scroll locked when modals close
     */

    // Don't render if no screenshots (but DO render when closed to allow proper cleanup)
    if (screenshots.length === 0) return null;

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            size="xl"
            contentClassName="screenshot-lightbox"
            disableOverlayDismiss={false}
            usePortal={true}
        >
            <div className="screenshot-lightbox__container">
                {/* Close button */}
                <button
                    className="screenshot-lightbox__close"
                    onClick={onClose}
                    aria-label="Close lightbox"
                >
                    ✕
                </button>

                {/* Title and description above image */}
                {(title || description) &&
                    <div className="screenshot-lightbox__header">
                        {title && <h3 className="screenshot-lightbox__title">{title}</h3>}
                        {description && <p className="screenshot-lightbox__description">{formatDescription(description)}</p>}
                    </div>
                }

                {/* Image container */}
                <div className="screenshot-lightbox__image-container">
                    <img
                        src={screenshots[currentIndex]}
                        alt={`Screenshot ${currentIndex + 1}`}
                        className="screenshot-lightbox__image"
                    />
                </div>

                {/* Navigation buttons */}
                {screenshots.length > 1 &&
                    <>
                        <button
                            className="screenshot-lightbox__nav screenshot-lightbox__nav--prev"
                            onClick={handlePrevious}
                            aria-label="Previous screenshot"
                        >
                            <FaChevronLeft />
                        </button>
                        <button
                            className="screenshot-lightbox__nav screenshot-lightbox__nav--next"
                            onClick={handleNext}
                            aria-label="Next screenshot"
                        >
                            <FaChevronRight />
                        </button>
                    </>
                }

                {/* Image counter */}
                <div className="screenshot-lightbox__counter">
                    {currentIndex + 1} of {screenshots.length}
                </div>

                {/* Thumbnail navigation */}
                {screenshots.length > 1 &&
                    <div className="screenshot-lightbox__thumbnails">
                        {screenshots.map((screenshot, index) =>
                            <button
                                key={index}
                                className={`screenshot-lightbox__thumbnail ${
                                    index === currentIndex ? "screenshot-lightbox__thumbnail--active" : ""
                                }`}
                                onClick={() => setCurrentIndex(index)}
                                aria-label={`Go to screenshot ${index + 1}`}
                            >
                                <img
                                    src={screenshot}
                                    alt={`Screenshot ${index + 1} thumbnail`}
                                />
                            </button>)}
                    </div>
                }
            </div>
        </Modal>
    );
};

export default ScreenshotLightbox;